import { type PointerEvent as ReactPointerEvent, useEffect, useRef } from 'react';
import type { ConversationDockPreview } from '@/lib/conversations/conversation-layout';
import { panelSplitPreviewAt } from '@/lib/desktop-shell/panel-split';
import type { StateSetter } from '@/lib/desktop-shell/store';
import {
  closePanelTab,
  movePanelTab,
  type PanelTabs,
  retainPanelTabs,
} from '@/lib/side-panel/panel-tabs';
import { showPanelSplitPreview } from '@/lib/side-panel/split-drag';
import { animateTabReorder, captureTabPositions } from '@/lib/side-panel/tab-drag';
import { useActiveTabScroll } from '@/lib/side-panel/use-active-tab-scroll';

/** Splitting the panel by dragging a tab over the view shown, and back onto the tab bar. */
export type PanelTabSplit = {
  drop(id: string, preview: ConversationDockPreview): void;
  /** Dragged over the views and back onto the tab bar: the tab leaves the split. */
  unsplit(id: string): void;
};

/**
 * The side panel's tab strip over its {@link PanelTabs}: closing a tab (the last one closes the
 * panel), dropping the tabs of panels that are no longer available, and dragging tabs to reorder
 * them, or over the view shown to split the panel.
 */
export function usePanelTabs({
  availablePluginViews,
  onClose,
  setTabs,
  split,
  tabs,
}: {
  split?: PanelTabSplit | undefined;
  availablePluginViews?: ReadonlySet<string>;
  onClose: () => void;
  setTabs: StateSetter<PanelTabs>;
  tabs: PanelTabs;
}) {
  const tabScrollRef = useRef<HTMLDivElement>(null);

  function closeTab(id: string): void {
    const next = closePanelTab(tabs, id);
    setTabs(next);
    // Opened again, the panel offers its launcher.
    if (next.tabs.length === 0) onClose();
  }

  useEffect(() => {
    if (availablePluginViews === undefined) return;
    setTabs((current) =>
      retainPanelTabs(
        current,
        (tab) => !tab.view.startsWith('plugin:') || availablePluginViews.has(tab.view),
      ),
    );
  }, [availablePluginViews, setTabs]);

  useActiveTabScroll({ activeId: tabs.activeId, tabScrollRef, tabs: tabs.tabs });

  function moveTab(draggedId: string, targetId: string): void {
    if (draggedId === targetId) return;
    const previousPositions = captureTabPositions(tabScrollRef.current);
    setTabs((current) => movePanelTab(current, draggedId, targetId));
    requestAnimationFrame(() =>
      animateTabReorder(tabScrollRef.current, previousPositions, draggedId),
    );
  }

  function beginTabDrag(event: ReactPointerEvent<HTMLDivElement>, id: string): void {
    if ((event.target as HTMLElement).closest('[data-tab-close]') !== null) return;
    const startX = event.clientX;
    const startY = event.clientY;
    const draggedElement = event.currentTarget;
    const initialLeft = draggedElement.getBoundingClientRect().left;
    let dragging = false;
    let lastTargetId = id;
    let translateX = 0;
    let splitPreview: ConversationDockPreview | null = null;
    let overViews = false;

    function pointerMove(pointerEvent: PointerEvent): void {
      if (!dragging && Math.hypot(pointerEvent.clientX - startX, pointerEvent.clientY - startY) < 4)
        return;
      if (!dragging && split !== undefined) {
        // Over an app's frame, the page only gets the pointer while it is captured.
        try {
          draggedElement.setPointerCapture(pointerEvent.pointerId);
        } catch {
          // A pointer already released cannot be captured: the drop ends the drag anyway.
        }
      }
      dragging = true;
      pointerEvent.preventDefault();
      if (split !== undefined) {
        // Where the tab would go with the pointer there; null keeps it in the strip.
        const preview = panelSplitPreviewAt(pointerEvent.clientX, pointerEvent.clientY);
        if (preview?.targetId !== splitPreview?.targetId || preview?.side !== splitPreview?.side) {
          splitPreview = preview;
          showPanelSplitPreview(preview);
        }
        if (preview !== null) {
          overViews = true;
          draggedElement.style.opacity = '0.5';
          return;
        }
        draggedElement.style.removeProperty('opacity');
      }
      document.body.style.userSelect = 'none';
      const renderedLeft = draggedElement.getBoundingClientRect().left;
      const layoutLeft = renderedLeft - translateX;
      translateX = pointerEvent.clientX - startX - (layoutLeft - initialLeft);
      draggedElement.style.transform = `translateX(${translateX}px)`;
      draggedElement.style.zIndex = '20';
      draggedElement.style.boxShadow = 'var(--desktop-shadow-drag)';
      draggedElement.style.opacity = '0.9';
      const target = document
        .elementsFromPoint(pointerEvent.clientX, pointerEvent.clientY)
        .map((element) => element.closest<HTMLElement>('[data-panel-tab-id]'))
        .find((tab) => tab !== null && tab.dataset['panelTabId'] !== id);
      const targetId = target?.dataset['panelTabId'];
      if (targetId !== undefined && targetId !== lastTargetId) {
        lastTargetId = targetId;
        moveTab(id, targetId);
      }
    }

    function pointerUp(pointerEvent: PointerEvent): void {
      document.removeEventListener('pointermove', pointerMove);
      document.removeEventListener('pointerup', pointerUp);
      document.body.style.removeProperty('user-select');
      if (!dragging) return;
      const bar = tabScrollRef.current?.getBoundingClientRect();
      const backOnBar =
        overViews &&
        bar !== undefined &&
        pointerEvent.clientY >= bar.top &&
        pointerEvent.clientY <= bar.bottom;
      if ((splitPreview !== null || backOnBar) && split !== undefined) {
        showPanelSplitPreview(null);
        for (const property of ['transform', 'z-index', 'box-shadow', 'opacity'])
          draggedElement.style.removeProperty(property);
        // The click ending the drag would select the tab again.
        const swallow = (click: MouseEvent) => {
          click.stopPropagation();
          click.preventDefault();
        };
        window.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
        if (splitPreview !== null) split.drop(id, splitPreview);
        else split.unsplit(id);
        return;
      }
      const settle = draggedElement.animate(
        [{ transform: `translateX(${translateX}px)` }, { transform: 'translateX(0)' }],
        { duration: 160, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
      );
      draggedElement.style.removeProperty('transform');
      settle.addEventListener(
        'finish',
        () => {
          draggedElement.style.removeProperty('z-index');
          draggedElement.style.removeProperty('box-shadow');
          draggedElement.style.removeProperty('opacity');
        },
        { once: true },
      );
    }

    document.addEventListener('pointermove', pointerMove);
    document.addEventListener('pointerup', pointerUp, { once: true });
  }

  function tabDragProps(id: string) {
    return {
      'data-panel-tab-id': id,
      onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => beginTabDrag(event, id),
    };
  }

  return { closeTab, tabDragProps, tabScrollRef };
}
